import { InvoiceItem } from '../types/invoiceTypes';
import { ProductTranslations } from '../types/translationTypes';

export interface Product extends Omit<InvoiceItem, 'quantity'> {
  id: string;
  createdAt?: any; // Firestore Timestamp
  updatedAt?: any; // Firestore Timestamp
  translations?: Record<string, ProductTranslations>;
}

export interface SortConfig {
  key: keyof Product | '';
  direction: 'asc' | 'desc';
}

/**
 * Formatiert einen Preis in Euro mit 2 Nachkommastellen
 */
export const formatPrice = (price: number | null | undefined): string => {
  // Prüfe, ob der Preis ein gültiger numerischer Wert ist
  if (typeof price === 'number' && !isNaN(price)) {
    return price.toFixed(2).replace('.', ',') + ' €';
  }
  // Fallback für ungültige oder fehlende Preise
  return '0,00 €';
};

/**
 * Formatiert einen Steuersatz in Prozent mit 1 Nachkommastelle
 */
export const formatTaxRate = (rate: number | null | undefined): string => {
  // Prüfe, ob der Steuersatz ein gültiger numerischer Wert ist
  if (typeof rate === 'number' && !isNaN(rate)) {
    return rate.toFixed(1).replace('.', ',') + ' %';
  }
  // Fallback für ungültige oder fehlende Steuersätze
  return '0,0 %';
};

/**
 * Standardisiert ein Produkt für die Speicherung
 * Entfernt null/undefined Werte, stellt sicher dass numerische Werte
 * korrekt als Zahlen gespeichert werden und fügt Standardwerte hinzu
 */
export const standardizeProduct = (productData: Partial<Product>): any => {
  // Wir entfernen zunächst alle Felder, die null oder undefined sind
  const cleanedProduct: any = {};
  
  // Alle Felder kopieren, die nicht null/undefined sind
  Object.entries(productData).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      cleanedProduct[key] = value;
    }
  });
  
  // Sicherstellen, dass numerische Werte wirklich Zahlen sind
  if (cleanedProduct.netPrice) {
    cleanedProduct.netPrice = Number(cleanedProduct.netPrice);
  }
  
  if (cleanedProduct.taxRate) {
    cleanedProduct.taxRate = Number(cleanedProduct.taxRate);
  }
  
  // Sicherstellen, dass obligatorische Felder vorhanden sind und korrekte Datentypen haben
  const standardizedProduct = {
    // Pflichtfelder mit Standardwerten
    name: cleanedProduct.name || "",
    description: cleanedProduct.description || "",
    netPrice: cleanedProduct.netPrice || 0,
    taxRate: cleanedProduct.taxRate || 19,
    unitCode: cleanedProduct.unitCode || "C62",
    sellerArticleId: cleanedProduct.sellerArticleId || "", 
    
    // Weitere Felder, die aus dem Original kopiert werden, wenn vorhanden
    ...cleanedProduct,
    
    // Zeitstempel-Felder immer überschreiben
    createdAt: cleanedProduct.createdAt || new Date(),
    updatedAt: new Date()
  };
  
  // Entfernen spezifischer Felder, die nicht Teil des Produkts sein sollen
  delete standardizedProduct.id; // ID wird separat verwaltet
  delete standardizedProduct.grossPrice; // Wird nur im UI gebraucht, nicht gespeichert
  delete standardizedProduct.lastUsed; // Falls vorhanden, nicht speichern
  delete standardizedProduct.quantity; // Nicht Teil des Produkts, sondern des LineItems
  
  return standardizedProduct;
}; 